'use server'

import { apiRequest, ApiResponse } from '../request'
import type { HealthPlanSettlementMode } from '../common/interfaces/billing.interface'

export type { HealthPlanSettlementMode }

export interface HealthPlan {
    id: number
    name: string
    ansCode?: string
    cnpj?: string
    phone?: string
    email?: string
    website?: string
    planType?: string
    coverage?: string[]
    /** Como o convênio é faturado. TISS_BATCH ainda não é suportado. */
    settlementMode?: HealthPlanSettlementMode
    /** Dia do fechamento da competência (1-28). Vazio = último dia do mês. */
    invoiceClosingDay?: number | null
    /** Prazo de pagamento acordado, em dias corridos a partir do fechamento. */
    paymentTermDays?: number | null
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    contactName?: string
    contactPhone?: string
    contactEmail?: string
    notes?: string
    logoUrl?: string
    active: boolean
    organizationId: number
    organization?: {
        id: number
        name: string
        tradingName?: string
    }
    _count?: {
        clients: number
    }
    createdAt: string
    updatedAt: string
}

export interface CreateHealthPlanData {
    name: string
    ansCode?: string
    cnpj?: string
    phone?: string
    email?: string
    website?: string
    planType?: string
    coverage?: string[]
    /** Como o convênio é faturado. TISS_BATCH ainda não é suportado. */
    settlementMode?: HealthPlanSettlementMode
    /** Dia do fechamento da competência (1-28). Vazio = último dia do mês. */
    invoiceClosingDay?: number | null
    /** Prazo de pagamento acordado, em dias corridos a partir do fechamento. */
    paymentTermDays?: number | null
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    contactName?: string
    contactPhone?: string
    contactEmail?: string
    notes?: string
    logoUrl?: string
    active?: boolean
    organizationId: number
}

export interface UpdateHealthPlanData {
    name?: string
    ansCode?: string
    cnpj?: string
    phone?: string
    email?: string
    website?: string
    planType?: string
    coverage?: string[]
    /** Como o convênio é faturado. TISS_BATCH ainda não é suportado. */
    settlementMode?: HealthPlanSettlementMode
    /** Dia do fechamento da competência (1-28). Vazio = último dia do mês. */
    invoiceClosingDay?: number | null
    /** Prazo de pagamento acordado, em dias corridos a partir do fechamento. */
    paymentTermDays?: number | null
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    contactName?: string
    contactPhone?: string
    contactEmail?: string
    notes?: string
    logoUrl?: string
    active?: boolean
}

export interface ClientHealthPlan {
    id: number
    clientId: number
    healthPlanId: number
    cardNumber: string
    planType?: string
    validUntil?: string
    holderName?: string
    holderCpf?: string
    relationship?: string
    isPrimary: boolean
    healthPlan?: HealthPlan
    createdAt: string
    updatedAt: string
}

export interface CreateClientHealthPlanData {
    healthPlanId: number
    cardNumber: string
    planType?: string
    validUntil?: string
    holderName?: string
    holderCpf?: string
    relationship?: string
    isPrimary?: boolean
}

export interface UpdateClientHealthPlanData {
    healthPlanId?: number
    cardNumber?: string
    planType?: string
    validUntil?: string
    holderName?: string
    holderCpf?: string
    relationship?: string
    isPrimary?: boolean
}

// ==================== HEALTH PLAN CRUD ====================

export async function getHealthPlans(
    organizationId?: number
): Promise<ApiResponse<HealthPlan[]>> {
    const params = organizationId ? { organizationId } : {}
    return apiRequest<HealthPlan[]>('medical/health-plans', { params })
}

export async function getHealthPlan(
    id: number
): Promise<ApiResponse<HealthPlan>> {
    return apiRequest<HealthPlan>(`medical/health-plans/${id}`, {})
}

export async function createHealthPlan(
    data: CreateHealthPlanData
): Promise<ApiResponse<HealthPlan>> {
    return apiRequest<HealthPlan>('medical/health-plans', { data }, 'post')
}

export async function updateHealthPlan(
    id: number,
    data: UpdateHealthPlanData
): Promise<ApiResponse<HealthPlan>> {
    return apiRequest<HealthPlan>(
        `medical/health-plans/${id}`,
        { data },
        'patch'
    )
}

export async function deleteHealthPlan(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`medical/health-plans/${id}`, {}, 'delete')
}

// ==================== CLIENT HEALTH PLAN ====================

export async function getClientHealthPlans(
    clientId: number
): Promise<ApiResponse<ClientHealthPlan[]>> {
    return apiRequest<ClientHealthPlan[]>(
        `medical/health-plans/client/${clientId}`,
        {}
    )
}

export async function createClientHealthPlan(
    clientId: number,
    data: CreateClientHealthPlanData
): Promise<ApiResponse<ClientHealthPlan>> {
    return apiRequest<ClientHealthPlan>(
        `medical/health-plans/client/${clientId}`,
        { data },
        'post'
    )
}

export async function updateClientHealthPlan(
    clientId: number,
    planId: number,
    data: UpdateClientHealthPlanData
): Promise<ApiResponse<ClientHealthPlan>> {
    return apiRequest<ClientHealthPlan>(
        `medical/health-plans/client/${clientId}/${planId}`,
        { data },
        'patch'
    )
}

export async function deleteClientHealthPlan(
    clientId: number,
    planId: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `medical/health-plans/client/${clientId}/${planId}`,
        {},
        'delete'
    )
}
